import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, RefreshCw, Table } from 'lucide-react';
import * as XLSX from 'xlsx';

interface SpreadsheetPreviewProps {
  url: string;
  fileName: string;
}

export const SpreadsheetPreview: React.FC<SpreadsheetPreviewProps> = ({ url, fileName }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [sheetsData, setSheetsData] = useState<Record<string, any[][]>>({});

  const loadSpreadsheet = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });

      if (workbook.SheetNames.length === 0) {
        throw new Error('Spreadsheet contains no sheets');
      }

      const parsedSheets: Record<string, any[][]> = {};
      workbook.SheetNames.forEach((name) => {
        const sheet = workbook.Sheets[name];
        if (sheet) {
          const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as any[][];
          parsedSheets[name] = matrix;
        } else {
          parsedSheets[name] = [];
        }
      });

      setSheetNames(workbook.SheetNames);
      setActiveSheet(workbook.SheetNames[0] || '');
      setSheetsData(parsedSheets);
    } catch (err) {
      console.warn('[SpreadsheetPreview] Failed to parse spreadsheet:', err);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSpreadsheet();
  }, [url]);

  if (isLoading) {
    return (
      <div className="flex-1 w-full h-full bg-slate-950 flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        <p className="text-xs font-semibold">Parsing Excel workbook ({fileName})...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center max-w-md mx-auto gap-3">
        <AlertCircle className="h-10 w-10 text-amber-400" />
        <p className="font-bold text-foreground text-sm">Spreadsheet Preview Failed</p>
        <p className="text-xs text-muted-foreground">Unable to parse spreadsheet contents in browser.</p>
        <button
          type="button"
          onClick={loadSpreadsheet}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold text-xs transition mt-2"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          <span>Retry</span>
        </button>
      </div>
    );
  }

  const currentGrid = sheetsData[activeSheet] || [];
  const maxCols = Math.max(0, ...currentGrid.map((row) => row.length));
  const colLetters = Array.from({ length: Math.min(maxCols, 50) }, (_, i) => {
    return String.fromCharCode(65 + i);
  });

  const displayedRows = currentGrid.slice(0, 1000);

  return (
    <div className="flex-1 w-full h-full bg-slate-950 flex flex-col overflow-hidden text-slate-200 select-text">
      {/* Sheet Tabs Header */}
      <div className="flex items-center gap-1 p-2 bg-slate-900 border-b border-slate-800 overflow-x-auto select-none">
        {sheetNames.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setActiveSheet(name)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition ${
              activeSheet === name
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-xs'
                : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
            }`}
          >
            <Table className="h-3.5 w-3.5 inline-block mr-1.5 text-emerald-500" />
            {name}
          </button>
        ))}
      </div>

      {/* Grid Container */}
      <div className="flex-1 overflow-auto p-4">
        {displayedRows.length === 0 ? (
          <div className="p-8 text-center text-xs text-slate-500">Sheet "{activeSheet}" is empty.</div>
        ) : (
          <table className="w-full text-xs border-collapse text-left">
            <thead>
              <tr className="bg-slate-900 border-b border-slate-800 text-slate-400 font-mono select-none">
                <th className="p-2 border-r border-slate-800 w-12 text-center text-[11px]">#</th>
                {colLetters.map((col, idx) => (
                  <th key={idx} className="p-2.5 font-bold border-r border-slate-800 min-w-[100px] text-center">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedRows.map((row, rIdx) => (
                <tr key={rIdx} className="border-b border-slate-800/60 hover:bg-slate-900/50 font-mono">
                  <td className="p-2 border-r border-slate-800 text-slate-500 text-center select-none text-[11px] font-medium bg-slate-900/40">
                    {rIdx + 1}
                  </td>
                  {colLetters.map((_, cIdx) => (
                    <td key={cIdx} className="p-2.5 border-r border-slate-800/60 whitespace-nowrap truncate max-w-xs">
                      {row[cIdx] !== undefined ? String(row[cIdx]) : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {currentGrid.length > 1000 && (
        <div className="p-2 text-[11px] text-amber-400 bg-amber-500/10 border-t border-slate-800 text-center font-medium">
          Showing first 1,000 rows of {currentGrid.length} total rows. Download original file for full data.
        </div>
      )}
    </div>
  );
};
